<template>
  <div class="services-page">
    <section class="services-hero">
      <span class="label">Services</span>
      <h1 class="page-title">What We Do</h1>
      <p class="services-intro">
        Full-service fire protection — inspections, installations,
        emergency repairs, and compliance reporting.
      </p>
    </section>

    <!-- Service Tabs -->
    <section class="services-body">
      <div class="service-tabs">
        <button
          v-for="(service, i) in services"
          :key="i"
          class="tab"
          :class="{ active: activeService == i }"
          @click="setActive(i)"
        >
          <span class="tab-number">0{{ i + 1 }}</span>
          <span class="tab-name">{{ service.name }}</span>
        </button>
      </div>

      <div class="service-detail">
        <h2 class="detail-title">{{ services[activeService].name }}</h2>
        <p class="detail-text">{{ services[activeService].info }}</p>
        <ul class="detail-list">
          <li v-for="(item, j) in services[activeService].items" :key="j">
            {{ item }}
          </li>
        </ul>
        <a href="tel:7605673777" class="detail-cta">Call 760-567-3777 &rarr;</a>
      </div>
    </section>

    <!-- Distributors -->
    <section class="distributors-section">
      <span class="label">Partners</span>
      <div class="distributor-logos">
        <img src="../assets/certs/amerex.png" alt="Amerex" />
        <img src="../assets/img/brooks.png" alt="Brooks" />
        <img src="../assets/img/Ferguson.jpg" alt="Ferguson" />
        <img src="../assets/img/firelite-logo.png" alt="Fire-Lite" />
        <img src="../assets/img/potter-logo.png" alt="Potter" />
        <img src="../assets/img/silent-knight.png" alt="Silent Knight" />
        <img src="../assets/img/VikingLogo.png" alt="Viking" />
      </div>
    </section>
  </div>
</template>

<script>
export default {
  props: {
    activeService: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      services: [
        {
          name: "Fire Sprinkler Systems",
          info: "Well maintained and functioning fire sprinkler systems are an integral part of a company's overall fire safety plan. Our team's combined experience allows us to offer design, installation, preventative maintenance, and emergency services.",
          items: [
            "Inspections & testing (NFPA 25)",
            "Repairs & system troubleshooting",
            "New installations (C-16 Licensed)",
            "Underground & standpipe inspections",
          ],
        },
        {
          name: "Extinguishers & Kitchen Suppression",
          info: "Portable fire extinguishers provide a first line of defense. Kitchen suppression systems protect commercial kitchens where equipment heats to extreme temperatures and oils burn hotter. We service all major brands in accordance with NFPA 10, NFPA 17A, and Title 19.",
          items: [
            "Annual fire extinguisher inspections",
            "Sales & replacements",
            "Kitchen suppression systems (Ansul, Pyro-Chem, Amerex)",
            "Semi-annual inspections & repairs",
          ],
        },
        {
          name: "Life Safety Systems",
          info: "Life safety systems are critical for building occupant protection. We ensure your emergency lighting and exit signage meet code requirements and function reliably when needed most.",
          items: [
            "Emergency lighting inspections",
            "Exit sign testing & repairs",
            "Battery backup replacements",
          ],
        },
        {
          name: "Testing Services",
          info: "Regular testing ensures your fire protection systems perform correctly in an emergency. Our certified technicians conduct thorough testing per NFPA standards with detailed documentation.",
          items: [
            "OS&Y valve testing",
            "Hydrant flow testing & flushing",
            "Fire pump inspections",
          ],
        },
        {
          name: "Repairs & Code Compliance",
          info: "When deficiencies are found during inspections, our team can handle all necessary repairs and upgrades to bring your systems into full compliance with current fire codes.",
          items: [
            "Deficiency corrections",
            "Valve replacements (OS&Y, PIV)",
            "Leak repairs & system upgrades",
            "Code compliance upgrades",
          ],
        },
        {
          name: "Compliance & Reporting",
          info: "We handle all documentation and reporting requirements so you stay in good standing with fire marshals, insurance carriers, and compliance platforms.",
          items: [
            "AES / Compliance Engine reporting",
            "Fire Marshal documentation",
            "Detailed inspection reports",
            "Insurance compliance support",
          ],
        },
      ],
    };
  },
  methods: {
    open() {
      this.$emit("showQuote", true);
    },
    setActive(index) {
      this.activeService = index;
      window.scrollTo(0, 0);
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },
  },
  mounted() {
    this.scrollToTop();
  },
};
</script>

<style scoped>
.services-page {
  padding-bottom: 0;
}

.label {
  display: block;
  font-family: var(--font-display);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--color-fire);
  margin-bottom: 1.5rem;
}

.services-hero {
  padding: 5rem clamp(2rem, 8vw, 8rem) 3rem;
}

.page-title {
  font-family: var(--font-display);
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 0.75rem;
}

.services-intro {
  max-width: 480px;
  color: var(--color-text-muted);
  font-size: 1rem;
  line-height: 1.7;
}

/* ── TABS ── */
.services-body {
  max-width: 900px;
  padding: 0 clamp(2rem, 8vw, 8rem) 5rem;
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 2rem;
}

.service-tabs {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.tab {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  border-left: 2px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  color: var(--color-text-muted);
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 500;
}

.tab:hover {
  color: var(--color-text);
}

.tab.active {
  border-left-color: var(--color-fire);
  color: var(--color-text);
}

.tab-number {
  font-family: var(--font-display);
  font-size: 0.7rem;
  opacity: 0.3;
}

/* ── DETAIL ── */
.service-detail {
  padding-top: 0.5rem;
}

.detail-title {
  font-family: var(--font-display);
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  letter-spacing: -0.01em;
}

.detail-text {
  color: var(--color-text-muted);
  line-height: 1.8;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
}

.detail-list {
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
}

.detail-list li {
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--color-border);
  font-size: 0.875rem;
  color: var(--color-text);
}

.detail-list li:last-child {
  border-bottom: none;
}

.detail-cta {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-fire);
  text-decoration: none;
  transition: opacity 0.2s;
}

.detail-cta:hover {
  opacity: 0.8;
}

/* ── DISTRIBUTORS ── */
.distributors-section {
  padding: 5rem clamp(2rem, 8vw, 8rem);
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
}

.distributor-logos {
  display: flex;
  flex-wrap: wrap;
  gap: 2.5rem;
  align-items: center;
}

.distributor-logos img {
  height: 40px;
  filter: grayscale(1) brightness(1.5);
  opacity: 0.5;
  transition: all 0.3s;
}

.distributor-logos img:hover {
  filter: none;
  opacity: 1;
}

/* ── RESPONSIVE ── */
@media (max-width: 768px) {
  .services-body {
    grid-template-columns: 1fr;
  }
  .service-tabs {
    flex-direction: row;
    overflow-x: auto;
    gap: 0;
    border-bottom: 1px solid var(--color-border);
    margin-bottom: 1rem;
  }
  .tab {
    border-left: none;
    border-bottom: 2px solid transparent;
    white-space: nowrap;
    flex-shrink: 0;
    padding: 0.75rem 1rem;
  }
  .tab.active {
    border-left-color: transparent;
    border-bottom-color: var(--color-fire);
  }
  .tab-number {
    display: none;
  }
}
</style>
